import { BaseService, ServiceResponse, PaginatedResponse } from './base.service';
import type {
  ClubChannel,
  ClubChannelMessage,
  ClubChannelMember,
  MessageReaction,
  MessageMention,
  CreateChannelRequest,
  SendMessageRequest,
  UpdateMessageRequest,
  ChannelFilters,
  MessageFilters,
  ChannelMemberFilters,
  ChannelSubscriptionCallback,
  ReactionSubscriptionCallback,
  TypingSubscriptionCallback,
  TypingIndicator,
  UserProfile,
  ClubRole,
  DEFAULT_CHANNELS
} from '../../types/club-community';

export class ClubCommunityService extends BaseService {
  private static instance: ClubCommunityService;
  private subscriptions: Map<string, any> = new Map();
  private typingTimers: Map<string, NodeJS.Timeout> = new Map();

  static getInstance(): ClubCommunityService {
    if (!ClubCommunityService.instance) {
      ClubCommunityService.instance = new ClubCommunityService();
    }
    return ClubCommunityService.instance;
  }

  // =====================
  // CHANNEL MANAGEMENT
  // =====================

  /**
   * Get channels accessible to staff members
   * Staff can participate in all public channels plus staff-only channels
   */
  async getStaffAccessibleChannels(clubId: string): Promise<ServiceResponse<ClubChannel[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user is staff member
      const { data: staffMembership } = await this.supabase
        .from('user_club_memberships')
        .select('id, staff_role, role')
        .eq('user_id', user.id)
        .eq('club_id', clubId)
        .not('staff_role', 'is', null)
        .single();

      if (!staffMembership) {
        throw new Error('User is not a staff member of this club');
      }

      // Get all channels the staff member can access
      const { data: channels, error } = await this.supabase
        .from('club_channels')
        .select(`
          *,
          message_count:club_channel_messages(count),
          last_message:club_channel_messages(
            id, created_at, content, sender_id,
            sender:profiles!sender_id(display_name)
          )
        `)
        .eq('club_id', clubId)
        .order('position', { ascending: true });

      if (error) throw error;

      // Process channels with message count and last message
      const processedChannels = channels?.map(channel => ({
        ...channel,
        message_count: Array.isArray(channel.message_count) ? channel.message_count.length : 0,
        last_message_at: channel.last_message?.[0]?.created_at || null,
        member_count: 0 // Will be populated by separate query if needed
      })) || [];

      return processedChannels;
    });
  }

  /**
   * Send message as staff member in channel with Contact History logging
   */
  async sendStaffMessageToChannel(data: {
    channelId: string;
    staffMembershipId: string;
    message: string;
    replyToMessageId?: string;
    isContactHistoryEligible?: boolean;
    memberMembershipId?: string; // Required if isContactHistoryEligible is true
  }): Promise<ServiceResponse<ClubChannelMessage & { contactHistoryLogged?: boolean }>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify staff membership
      const { data: staffMembership } = await this.supabase
        .from('user_club_memberships')
        .select('id, staff_role, club_id')
        .eq('id', data.staffMembershipId)
        .eq('user_id', user.id)
        .not('staff_role', 'is', null)
        .single();

      if (!staffMembership) {
        throw new Error('Invalid staff membership');
      }

      // Send the message using existing sendMessage method
      const messageResponse = await this.sendMessage({
        channel_id: data.channelId,
        content: data.message,
        reply_to_id: data.replyToMessageId,
        thread_root_id: data.replyToMessageId,
        message_type: 'text'
      });

      if (!messageResponse.success) {
        throw new Error('Failed to send message');
      }

      let contactHistoryLogged = false;

      // Log to Contact History if this is a staff reply to a member

      if (data.isContactHistoryEligible && data.memberMembershipId) {
        try {
          const { contactHistoryService } = await import('./contact-history.service');
          await contactHistoryService.logStaffReply({
            messageId: messageResponse.data.id,
            staffMembershipId: data.staffMembershipId,
            memberMembershipId: data.memberMembershipId,
            replyContent: data.message,
            clubId: staffMembership.club_id
          });
          contactHistoryLogged = true;
        } catch (error) {
          console.warn('Failed to log to contact history:', error);
          // Don't fail the message sending if contact history logging fails
        }
      }

      return {
        ...messageResponse.data,
        contactHistoryLogged
      };
    });
  }

  /**
   * Get channel participants with staff/member distinction
   */
  async getChannelParticipants(channelId: string): Promise<ServiceResponse<{
    staff: Array<{
      id: string;
      name: string;
      role: string;
      staff_role: string;
      avatar_url?: string;
      last_seen?: string;
      is_online?: boolean;
    }>;
    members: Array<{
      id: string;
      name: string;
      avatar_url?: string;
      handicap?: number;
      last_seen?: string;
      is_online?: boolean;
    }>;
  }>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get channel info to verify access
      const { data: channel } = await this.supabase
        .from('club_channels')
        .select('club_id')
        .eq('id', channelId)
        .single();

      if (!channel) throw new Error('Channel not found');

      // Get all club members who have access to this channel
      const { data: allMembers, error } = await this.supabase
        .from('user_club_memberships')
        .select(`
          id,
          role,
          staff_role,
          profiles!user_id(
            id,
            display_name,
            avatar_url,
            handicap
          )
        `)
        .eq('club_id', channel.club_id);

      if (error) throw error;

      // Separate staff and regular members
      const staff: any[] = [];
      const members: any[] = [];

      allMembers?.forEach(membership => {
        const profile = membership.profiles;
        if (!profile) return;

        const memberData = {
          id: membership.id,
          name: profile.display_name || 'Unknown',
          avatar_url: profile.avatar_url,
          last_seen: undefined, // Column doesn't exist yet
          is_online: false // Will be implemented later with real-time presence
        };

        if (membership.staff_role) {
          // This is a staff member
          staff.push({
            ...memberData,
            role: membership.role,
            staff_role: membership.staff_role
          });
        } else {
          // This is a regular member
          members.push({
            ...memberData,
            handicap: profile.handicap
          });
        }
      });

      return { staff, members };
    });
  }

  /**
   * Check if a message is from a staff member replying to a member
   */
  async detectStaffReplyToMember(messageId: string): Promise<ServiceResponse<{
    isStaffReply: boolean;
    staffMembershipId?: string;
    memberMembershipId?: string;
    originalMessageId?: string;
  }>> {
    return this.withErrorHandling(async () => {
      // Get the message details
      const { data: message, error } = await this.supabase
        .from('club_channel_messages')
        .select(`
          id,
          sender_id,
          reply_to_id,
          channel_id,
          channel:club_channels!channel_id(club_id)
        `)
        .eq('id', messageId)
        .single();

      if (error) throw error;
      if (!message) return { isStaffReply: false };

      // Check if sender is staff
      const { data: senderMembership } = await this.supabase
        .from('user_club_memberships')
        .select('id, staff_role')
        .eq('user_id', message.sender_id)
        .eq('club_id', message.channel.club_id)
        .single();

      if (!senderMembership?.staff_role) {
        return { isStaffReply: false };
      }

      // If this is a reply, check if original message was from a member
      if (message.reply_to_id) {
        const { data: originalMessage } = await this.supabase
          .from('club_channel_messages')
          .select('sender_id')
          .eq('id', message.reply_to_id)
          .single();

        if (originalMessage) {
          const { data: originalSenderMembership } = await this.supabase
            .from('user_club_memberships')
            .select('id, staff_role')
            .eq('user_id', originalMessage.sender_id)
            .eq('club_id', message.channel.club_id)
            .single();

          // If original sender is not staff (regular member), this is a staff reply to member
          if (originalSenderMembership && !originalSenderMembership.staff_role) {
            return {
              isStaffReply: true,
              staffMembershipId: senderMembership.id,
              memberMembershipId: originalSenderMembership.id,
              originalMessageId: message.reply_to_id
            };
          }
        }
      }

      return { isStaffReply: false };
    });
  }

  /**
   * Ensure default channels exist for a club
   */
  private async ensureDefaultChannels(clubId: string): Promise<void> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if the club already has channels
    const { data: existingChannels } = await this.supabase
      .from('club_channels')
      .select('id')
      .eq('club_id', clubId)
      .limit(1);

    if (existingChannels && existingChannels.length > 0) {
      return; // Channels already exist
    }

    // Create default channels with appropriate permissions
    const defaultChannels = [
      { 
        name: 'general', 
        channel_type: 'text', 
        description: 'General club discussion', 
        position: 1,
        member_can_post: true,
        admin_only: false
      },
      { 
        name: 'course-tips', 
        channel_type: 'advice', 
        description: 'Share course tips and advice', 
        position: 2,
        member_can_post: true,
        admin_only: false
      },
      { 
        name: 'playing-today', 
        channel_type: 'events', 
        description: 'Coordinate games and meetups', 
        position: 3,
        member_can_post: true,
        admin_only: false
      },
      { 
        name: 'round-shares', 
        channel_type: 'round_shares', 
        description: 'Completed round sharing', 
        position: 4,
        member_can_post: true,
        admin_only: false
      },
      { 
        name: 'announcements', 
        channel_type: 'announcements', 
        description: 'Club announcements', 
        position: 5,
        member_can_post: false,  // Read-only for members
        admin_only: false
      }
    ] as const;

    for (const channelData of defaultChannels) {
      await this.supabase
        .from('club_channels')
        .insert({
          club_id: clubId,
          name: channelData.name,
          channel_type: channelData.channel_type,
          description: channelData.description,
          position: channelData.position,
          is_private: false,
          is_default: true,
          member_can_post: channelData.member_can_post,
          admin_only: channelData.admin_only,
          created_by: user.id
        });
    }
  }

  async getClubChannels(filters: ChannelFilters): Promise<ServiceResponse<ClubChannel[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Ensure default channels exist for this club
      await this.ensureDefaultChannels(filters.club_id);

      let query = this.supabase
        .from('club_channels')
        .select(`
          *,
          club_channel_members!left(count)
        `)
        .eq('club_id', filters.club_id);

      // Filter by channel type if specified
      if (filters.channel_type) {
        query = query.eq('channel_type', filters.channel_type);
      }

      // Handle private channels - temporarily disable private channel logic
      query = query.eq('is_private', false);

      query = query.order('position').order('created_at');

      const { data, error } = await query;
      if (error) throw error;

      // Get unread counts for each channel
      const channelsWithCounts = await Promise.all((data || []).map(async (channel) => {
        const unreadCount = await this.getUnreadMessageCount(channel.id, user.id);
        const lastMessage = await this.getLastChannelMessage(channel.id);
        
        return {
          ...channel,
          unread_count: unreadCount,
          last_message: lastMessage,
          member_count: channel.club_channel_members?.[0]?.count || 0
        };
      }));

      return channelsWithCounts;
    });
  }

  async createChannel(data: CreateChannelRequest): Promise<ServiceResponse<ClubChannel>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user has permission to create channels in this club
      await this.verifyClubMembership(user.id, data.club_id);

      // Create the channel
      const { data: channel, error } = await this.supabase
        .from('club_channels')
        .insert({
          club_id: data.club_id,
          name: data.name,
          description: data.description,
          is_private: data.is_private || false,
          member_can_post: data.member_can_post !== undefined ? data.member_can_post : true,
          admin_only: data.admin_only || false,
          created_by: user.id,
          position: await this.getNextChannelPosition(data.club_id)
        })
        .select()
        .single();

      if (error) throw error;

      // If private channel, add invited members
      if (data.is_private && data.invited_members) {
        await this.addChannelMembers(channel.id, data.invited_members);
      }

      // Add creator to channel members if private
      if (data.is_private) {
        await this.addChannelMembers(channel.id, [user.id]);
      }

      // Send notification to club members
      await this.notifyClubMembers(
        data.club_id,
        'channel_created',
        'New Channel Created',
        `A new channel #${data.name} has been created in your club`,
        { channel_id: channel.id, channel_name: data.name }
      );

      return channel;
    });
  }

  async updateChannel(
    channelId: string, 
    updates: Partial<Pick<ClubChannel, 'name' | 'description' | 'member_can_post' | 'admin_only'>>
  ): Promise<ServiceResponse<ClubChannel>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user has permission to update this channel
      const { data: channel } = await this.supabase
        .from('club_channels')
        .select('created_by, club_id, is_default')
        .eq('id', channelId)
        .single();

      if (!channel) throw new Error('Channel not found');

      // Check if user is creator or club admin
      const isCreator = channel.created_by === user.id;
      const isClubAdmin = await this.isClubAdmin(user.id, channel.club_id);

      if (!isCreator && !isClubAdmin) {
        throw new Error('Permission denied');
      }

      const { data: updatedChannel, error } = await this.supabase
        .from('club_channels')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', channelId)
        .select()
        .single();

      if (error) throw error;
      return updatedChannel;
    });
  }

  async deleteChannel(channelId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify user has permission to delete this channel
      const { data: channel } = await this.supabase
        .from('club_channels')
        .select('created_by, club_id, is_default')
        .eq('id', channelId)
        .single();

      if (!channel) throw new Error('Channel not found');
      if (channel.is_default) throw new Error('Cannot delete default channels');

      // Check if user is creator or club admin
      const isCreator = channel.created_by === user.id;
      const isClubAdmin = await this.isClubAdmin(user.id, channel.club_id);

      if (!isCreator && !isClubAdmin) {
        throw new Error('Permission denied');
      }

      const { error } = await this.supabase
        .from('club_channels')
        .delete()
        .eq('id', channelId);

      if (error) throw error;

      // Send notification to club members
      await this.notifyClubMembers(
        channel.club_id,
        'channel_deleted',
        'Channel Deleted',
        `The channel #${channel.name} has been deleted`,
        { channel_name: channel.name }
      );

      return undefined;
    });
  }

  // =====================
  // MESSAGE MANAGEMENT
  // =====================

  async getChannelMessages(filters: MessageFilters): Promise<PaginatedResponse<ClubChannelMessage>> {
    return this.withPaginatedErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify access to channel
      await this.verifyChannelAccess(user.id, filters.channel_id);

      const limit = filters.limit || 50;
      const offset = filters.offset || 0;


      let query = this.supabase
        .from('club_channel_messages')
        .select(`
          *,
          sender:profiles!sender_id(id, display_name, avatar_url, handicap),
          reply_to:club_channel_messages!reply_to_id(
            id, content, sender_id,
            sender:profiles!sender_id(id, display_name, avatar_url)
          )
        `, { count: 'exact' })
        .eq('channel_id', filters.channel_id);

      if (filters.before_message_id) {
        const { data: beforeMessage } = await this.supabase
          .from('club_channel_messages')
          .select('created_at')
          .eq('id', filters.before_message_id)
          .single();
        
        if (beforeMessage) {
          query = query.lt('created_at', beforeMessage.created_at);
        }
      }

      if (filters.message_type) {
        query = query.eq('message_type', filters.message_type);
      }

      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;
      if (error) throw error;


      // Get reactions and mentions for each message
      const messagesWithExtras = await Promise.all((data || []).map(async (message) => {
        const [reactions, mentions] = await Promise.all([
          this.getMessageReactions(message.id),
          this.getMessageMentions(message.id)
        ]);

        // Build reaction counts and user reactions
        const reactionCounts: Record<string, number> = {};
        const userHasReacted: Record<string, boolean> = {};

        reactions.forEach(reaction => {
          reactionCounts[reaction.emoji] = (reactionCounts[reaction.emoji] || 0) + 1;
          if (reaction.user_id === user.id) {
            userHasReacted[reaction.emoji] = true;
          }
        });

        return {
          ...message,
          sender: message.sender, // Use the correctly joined profile data
          reactions,
          mentions,
          reaction_counts: reactionCounts,
          user_has_reacted: userHasReacted
        };
      }));

      return {
        data: messagesWithExtras.reverse(), // Return in chronological order
        pagination: {
          total: count || 0,
          page: Math.floor(offset / limit),
          limit,
          hasNextPage: (offset + limit) < (count || 0)
        }
      };
    });
  }

  async getAnnouncementReplyCounts(
    announcementIds: string[],
    channelId: string
  ): Promise<ServiceResponse<Record<string, { count: number; replierIds: string[] }>>> {
    return this.withErrorHandling(async () => {
      if (announcementIds.length === 0) return {};

      const { data, error } = await this.supabase
        .from('club_channel_messages')
        .select('id, reply_to_id, sender_id, created_at')
        .in('reply_to_id', announcementIds)
        .eq('channel_id', channelId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const result: Record<string, { count: number; replierIds: string[] }> = {};
      announcementIds.forEach(id => { result[id] = { count: 0, replierIds: [] }; });

      (data || []).forEach(row => {
        if (!row.reply_to_id) return;
        const entry = result[row.reply_to_id];
        if (!entry) return;
        entry.count += 1;
        if (entry.replierIds.length < 3 && !entry.replierIds.includes(row.sender_id)) {
          entry.replierIds.push(row.sender_id);
        }
      });

      return result;
    });
  }

  async getThreadReplies(
    announcementId: string,
    channelId: string
  ): Promise<ServiceResponse<ClubChannelMessage[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('club_channel_messages')
        .select(`
          *,
          sender:profiles!sender_id(id, display_name, avatar_url, handicap)
        `)
        .eq('reply_to_id', announcementId)
        .eq('channel_id', channelId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return (data || []).map(message => ({
        ...message,
        reactions: [],
        reaction_counts: {},
        user_has_reacted: {},
        mentions: []
      }));
    });
  }

  async sendMessage(data: SendMessageRequest): Promise<ServiceResponse<ClubChannelMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify access to channel
      await this.verifyChannelAccess(user.id, data.channel_id);

      // Create the message
      const { data: message, error } = await this.supabase
        .from('club_channel_messages')
        .insert({
          channel_id: data.channel_id,
          sender_id: user.id,
          content: data.content,
          message_type: data.message_type || 'text',
          message_data: data.message_data || {},
          reply_to_id: data.reply_to_id,
          thread_root_id: data.thread_root_id
        })
        .select(`
          *,
          sender:profiles!sender_id(id, display_name, avatar_url, handicap)
        `)
        .single();

      if (error) throw error;

      // Handle mentions
      if (data.mentions && data.mentions.length > 0) {
        await this.createMessageMentions(message.id, data.mentions);
      }

      // Update last read for sender
      await this.updateLastRead(data.channel_id, user.id);

      // Map sender_id to sender for compatibility
      return {
        ...message,
        sender: message.sender
      };
    });
  }

  async sendAdminMessage(data: SendMessageRequest & { admin_message_type?: 'announcement' | 'system' | 'update' }): Promise<ServiceResponse<ClubChannelMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify admin access to channel
      await this.verifyChannelAccess(user.id, data.channel_id);

      // Check if user is admin for this club
      const { data: channel } = await this.supabase
        .from('club_channels')
        .select('club_id')
        .eq('id', data.channel_id)
        .single();

      if (!channel) throw new Error('Channel not found');

      const isAdmin = await this.isClubAdmin(user.id, channel.club_id);
      if (!isAdmin) throw new Error('Only admins can send admin messages');

      // Create the admin message with special flags
      const { data: message, error } = await this.supabase
        .from('club_channel_messages')
        .insert({
          channel_id: data.channel_id,
          sender_id: user.id,
          content: data.content,
          message_type: data.message_type || 'announcement',
          message_data: {
            ...data.message_data,
            is_admin_message: true,
            admin_message_type: data.admin_message_type || 'announcement'
          },
          reply_to_id: data.reply_to_id
        })
        .select(`
          *,
          sender:profiles!sender_id(id, display_name, avatar_url, handicap)
        `)
        .single();

      if (error) throw error;

      // Handle mentions
      if (data.mentions && data.mentions.length > 0) {
        await this.createMessageMentions(message.id, data.mentions);
      }

      // Update last read for sender
      await this.updateLastRead(data.channel_id, user.id);

      // Mark message as admin message in response
      const adminMessage = {
        ...message,
        sender: message.sender,
        is_admin_message: true,
        admin_message_type: data.admin_message_type || 'announcement'
      };

      // Send notification for admin announcements
      if (data.message_type === 'announcement' || data.admin_message_type === 'announcement') {
        await this.notifyClubMembers(
          channel.club_id,
          'admin_announcement',
          'New Admin Announcement',
          `Admin announcement in #${await this.getChannelName(data.channel_id)}: ${data.content?.substring(0, 100)}${data.content && data.content.length > 100 ? '...' : ''}`,
          { 
            channel_id: data.channel_id, 
            message_id: message.id,
            admin_message_type: data.admin_message_type || 'announcement'
          }
        );
      }

      return adminMessage;
    });
  }

  async updateMessage(messageId: string, data: UpdateMessageRequest): Promise<ServiceResponse<ClubChannelMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: message, error } = await this.supabase
        .from('club_channel_messages')
        .update({
          content: data.content,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id)
        .select(`
          *,
          sender:profiles!sender_id(id, display_name, avatar_url, handicap)
        `)
        .single();

      if (error) throw error;
      // Map sender_id to sender for compatibility
      return {
        ...message,
        sender: message.sender
      };
    });
  }

  async deleteMessage(messageId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('club_channel_messages')
        .delete()
        .eq('id', messageId)
        .eq('sender_id', user.id);

      if (error) throw error;
      return undefined;
    });
  }

  // =====================
  // REACTIONS & MENTIONS
  // =====================

  async addReaction(messageId: string, emoji: string): Promise<ServiceResponse<MessageReaction>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: reaction, error } = await this.supabase
        .from('club_message_reactions')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          emoji
        })
        .select(`
          *,
          user:profiles(id, display_name, avatar_url)
        `)
        .single();

      if (error) throw error;
      return reaction;
    });
  }

  async removeReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('club_message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .eq('emoji', emoji);

      if (error) throw error;
      return undefined;
    });
  }

  // =====================
  // CHANNEL MEMBERS
  // =====================

  async getChannelMembers(filters: ChannelMemberFilters): Promise<ServiceResponse<ClubChannelMember[]>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      await this.verifyChannelAccess(user.id, filters.channel_id);

      let query = this.supabase
        .from('club_channel_members')
        .select(`
          *,
          user:profiles(id, display_name, avatar_url, handicap)
        `)
        .eq('channel_id', filters.channel_id);

      if (filters.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }

      query = query.order('joined_at');

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    });
  }

  async addChannelMembers(channelId: string, userIds: string[]): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify permission to add members
      await this.verifyChannelManagePermission(user.id, channelId);

      const members = userIds.map(userId => ({
        channel_id: channelId,
        user_id: userId
      }));

      const { error } = await this.supabase
        .from('club_channel_members')
        .upsert(members, {
          onConflict: 'channel_id,user_id',
          ignoreDuplicates: true
        });

      if (error) throw error;
      return undefined;
    });
  }

  async removeChannelMember(channelId: string, userId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Users can remove themselves, or managers can remove others
      const canRemove = userId === user.id || await this.verifyChannelManagePermission(user.id, channelId);
      if (!canRemove) throw new Error('Permission denied');

      const { error } = await this.supabase
        .from('club_channel_members')
        .update({ is_active: false })
        .eq('channel_id', channelId)
        .eq('user_id', userId);

      if (error) throw error;
      return undefined;
    });
  }

  // =====================
  // REAL-TIME SUBSCRIPTIONS
  // =====================

  subscribeToChannel(channelId: string, callback: ChannelSubscriptionCallback) {
    const channelName = `club-channel-${channelId}`;
    
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromChannel(channelId);
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'club_channel_messages',
        filter: `channel_id=eq.${channelId}`
      }, async (payload) => {
        // Fetch full message data with relations
        const { data: message } = await this.supabase
          .from('club_channel_messages')
          .select(`
            *,
            sender:profiles!sender_id(id, display_name, avatar_url, handicap),
            reply_to:club_channel_messages!reply_to_id(
              id, content, sender_id,
              sender:profiles!sender_id(id, display_name, avatar_url)
            )
          `)
          .eq('id', payload.new.id)
          .single();

        if (message) {
          // Get reactions and mentions
          const [reactions, mentions] = await Promise.all([
            this.getMessageReactions(message.id),
            this.getMessageMentions(message.id)
          ]);

          callback({
            ...message,
            sender: message.sender, // Use the correctly joined profile data
            reactions,
            mentions,
            reaction_counts: {},
            user_has_reacted: {}
          });
        }
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  subscribeToReactions(messageId: string, callback: ReactionSubscriptionCallback) {
    const channelName = `message-reactions-${messageId}`;
    
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromReactions(messageId);
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'club_message_reactions',
        filter: `message_id=eq.${messageId}`
      }, async (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
          const reactionId = payload.new?.id || payload.old?.id;
          if (!reactionId) return;

          const { data: reaction } = await this.supabase
            .from('club_message_reactions')
            .select('*')
            .eq('id', reactionId)
            .single();

          if (reaction) {
            // Fetch user profile separately
            const { data: profile } = await this.supabase
              .from('profiles')
              .select('id, display_name, avatar_url')
              .eq('id', reaction.user_id)
              .single();

            callback({
              ...reaction,
              user: profile || {
                id: reaction.user_id,
                display_name: 'Unknown',
                avatar_url: null
              }
            });
          }
        }
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  unsubscribeFromChannel(channelId: string) {
    const channelName = `club-channel-${channelId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.supabase.removeChannel(channel); // FIX: Add removeChannel
      this.subscriptions.delete(channelName);
    }
  }

  unsubscribeFromReactions(messageId: string) {
    const channelName = `message-reactions-${messageId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.supabase.removeChannel(channel); // FIX: Add removeChannel
      this.subscriptions.delete(channelName);
    }
  }

  unsubscribeAll() {
    console.log('🧹 [ClubCommunityService] Cleaning up all subscriptions');
    this.subscriptions.forEach((channel, name) => {
      try {
        channel.unsubscribe();
        this.supabase.removeChannel(channel); // FIX: Add removeChannel
      } catch (error) {
        console.error(`Error cleaning up channel ${name}:`, error);
      }
    });
    this.subscriptions.clear();
  }
  
  // FIX: Add destroy alias for consistency
  destroy() {
    this.unsubscribeAll();
  }

  // =====================
  // TYPING INDICATORS
  // =====================

  async sendTypingIndicator(channelId: string) {
    const key = `typing-${channelId}`;
    
    // Clear existing timer
    if (this.typingTimers.has(key)) {
      clearTimeout(this.typingTimers.get(key)!);
    }

    try {
      const userId = await this.getCurrentUserId();
      
      // Send typing indicator
      this.supabase
        .channel(`typing-${channelId}`)
        .send({
          type: 'broadcast',
          event: 'typing',
          payload: {
            user_id: userId,
            channel_id: channelId,
            timestamp: new Date().toISOString()
          }
        });

      // Set timer to stop typing after 3 seconds
      const timer = setTimeout(() => {
        this.stopTypingIndicator(channelId);
      }, 3000);

      this.typingTimers.set(key, timer);
    } catch (error) {
      console.error('Failed to send typing indicator:', error);
    }
  }

  async stopTypingIndicator(channelId: string) {
    const key = `typing-${channelId}`;
    
    if (this.typingTimers.has(key)) {
      clearTimeout(this.typingTimers.get(key)!);
      this.typingTimers.delete(key);
    }

    try {
      const userId = await this.getCurrentUserId();
      
      this.supabase
        .channel(`typing-${channelId}`)
        .send({
          type: 'broadcast',
          event: 'stop_typing',
          payload: {
            user_id: userId,
            channel_id: channelId,
            timestamp: new Date().toISOString()
          }
        });
    } catch (error) {
      console.error('Failed to stop typing indicator:', error);
    }
  }

  subscribeToTyping(channelId: string, callback: TypingSubscriptionCallback) {
    const channelName = `typing-${channelId}`;
    
    const channel = this.supabase
      .channel(channelName)
      .on('broadcast', { event: 'typing' }, async (payload) => {
        try {
          const userId = await this.getCurrentUserId();
          if (payload.payload.user_id !== userId) {
            callback(payload.payload as TypingIndicator);
          }
        } catch (error) {
          // If we can't get user ID, just process the typing indicator anyway
          callback(payload.payload as TypingIndicator);
        }
      })
      .on('broadcast', { event: 'stop_typing' }, (payload) => {
        // Handle stop typing if needed
      })
      .subscribe();

    this.subscriptions.set(`typing-subscription-${channelId}`, channel);
    return channel;
  }

  // =====================
  // HELPER METHODS
  // =====================

  private async verifyClubMembership(userId: string, clubId: string): Promise<void> {
    const { data } = await this.supabase
      .from('user_club_memberships')
      .select('id')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .eq('status', 'active')
      .single();

    if (!data) throw new Error('Not a member of this club');
  }

  private async verifyChannelAccess(userId: string, channelId: string): Promise<void> {
    const { data: channel } = await this.supabase
      .from('club_channels')
      .select('club_id, is_private')
      .eq('id', channelId)
      .single();

    if (!channel) throw new Error('Channel not found');

    // Verify club membership
    await this.verifyClubMembership(userId, channel.club_id);

    // For private channels, verify explicit membership
    if (channel.is_private) {
      const { data: membership } = await this.supabase
        .from('club_channel_members')
        .select('id')
        .eq('channel_id', channelId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      if (!membership) throw new Error('Access denied to private channel');
    }
  }

  private async verifyChannelManagePermission(userId: string, channelId: string): Promise<boolean> {
    const { data: channel } = await this.supabase
      .from('club_channels')
      .select('club_id, created_by')
      .eq('id', channelId)
      .single();

    if (!channel) return false;

    // Channel creator can manage
    if (channel.created_by === userId) return true;

    // Club admin can manage
    return await this.isClubAdmin(userId, channel.club_id);
  }

  private async isClubAdmin(userId: string, clubId: string): Promise<boolean> {
    const { data } = await this.supabase
      .from('user_club_memberships')
      .select('role')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .eq('status', 'active')
      .single();

    return data?.role === 'admin' || data?.role === 'owner';
  }

  private async getNextChannelPosition(clubId: string): Promise<number> {
    const { data } = await this.supabase
      .from('club_channels')
      .select('position')
      .eq('club_id', clubId)
      .order('position', { ascending: false })
      .limit(1)
      .single();

    return (data?.position || 0) + 1;
  }

  private async getUserAccessiblePrivateChannels(userId: string, clubId: string): Promise<string> {
    const { data } = await this.supabase
      .from('club_channel_members')
      .select('channel_id')
      .eq('user_id', userId)
      .eq('is_active', true);

    const channelIds = (data || []).map(m => m.channel_id);
    
    // If no private channels accessible, return a valid UUID that won't match anything
    // Using a nil UUID format to avoid syntax errors
    return channelIds.length > 0 ? channelIds.join(',') : '00000000-0000-0000-0000-000000000000';
  }

  async getUnreadMessageCount(channelId: string, userId: string): Promise<number> {
    const { data: member } = await this.supabase
      .from('club_channel_members')
      .select('last_read_at')
      .eq('channel_id', channelId)
      .eq('user_id', userId)
      .single();

    if (!member) return 0;

    const { count } = await this.supabase
      .from('club_channel_messages')
      .select('*', { count: 'exact', head: true })
      .eq('channel_id', channelId)
      .gt('created_at', member.last_read_at);

    return count || 0;
  }

  async getTotalUnreadCount(clubId: string, userId: string): Promise<number> {
    // Get all channels the user is a member of for this club
    const { data: channels } = await this.supabase
      .from('club_channels')
      .select('id')
      .eq('club_id', clubId);

    if (!channels || channels.length === 0) return 0;

    // Fetch last_read_at for all channels in one query
    const { data: memberships } = await this.supabase
      .from('club_channel_members')
      .select('channel_id, last_read_at')
      .eq('user_id', userId)
      .in('channel_id', channels.map(c => c.id));

    const lastReadMap = new Map<string, string>(
      (memberships || []).map(m => [m.channel_id, m.last_read_at])
    );

    // Count unread messages per channel and sum
    let total = 0;
    await Promise.all(channels.map(async (channel) => {
      const lastRead = lastReadMap.get(channel.id);
      let query = this.supabase
        .from('club_channel_messages')
        .select('*', { count: 'exact', head: true })
        .eq('channel_id', channel.id);

      if (lastRead) {
        query = query.gt('created_at', lastRead);
      }

      const { count } = await query;
      total += count || 0;
    }));

    return total;
  }

  private async getLastChannelMessage(channelId: string): Promise<ClubChannelMessage | null> {
    try {
      // First, get the message without the join
      const { data: messageData, error: messageError } = await this.supabase
        .from('club_channel_messages')
        .select('*')
        .eq('channel_id', channelId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (messageError || !messageData || messageData.length === 0) {
        return null;
      }

      const message = messageData[0];

      // Then, fetch the sender profile separately
      if (message.sender_id) {
        const { data: profileData } = await this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .eq('id', message.sender_id)
          .single();

        return {
          ...message,
          sender: profileData || { id: message.sender_id, display_name: 'Unknown', avatar_url: null }
        };
      }

      return {
        ...message,
        sender: null
      };
    } catch (error) {
      console.error('Error fetching last channel message:', error);
      return null;
    }
  }

  private async getMessageReactions(messageId: string): Promise<MessageReaction[]> {
    const { data } = await this.supabase
      .from('club_message_reactions')
      .select('*')
      .eq('message_id', messageId);

    if (!data || data.length === 0) return [];

    // Get unique user IDs
    const userIds = [...new Set(data.map(r => r.user_id).filter(Boolean))];
    
    // Batch query user profiles
    const userProfiles = new Map();
    if (userIds.length > 0) {
      const { data: profiles } = await this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', userIds);
      
      if (profiles) {
        profiles.forEach(profile => {
          userProfiles.set(profile.id, profile);
        });
      }
    }

    // Combine reactions with user data
    return data.map(reaction => ({
      ...reaction,
      user: userProfiles.get(reaction.user_id) || {
        id: reaction.user_id,
        display_name: 'Unknown',
        avatar_url: null
      }
    }));
  }

  private async getMessageMentions(messageId: string): Promise<MessageMention[]> {
    const { data } = await this.supabase
      .from('club_message_mentions')
      .select(`
        *,
        mentioned_user:profiles(id, display_name, avatar_url)
      `)
      .eq('message_id', messageId);

    return data || [];
  }

  private async createMessageMentions(messageId: string, userIds: string[]): Promise<void> {
    const mentions = userIds.map(userId => ({
      message_id: messageId,
      mentioned_user_id: userId
    }));

    await this.supabase
      .from('club_message_mentions')
      .insert(mentions);
  }

  // =====================
  // NOTIFICATION HELPERS
  // =====================

  /**
   * Send notifications to club members about channel changes
   */
  private async notifyClubMembers(
    clubId: string, 
    notificationType: 'channel_created' | 'channel_deleted' | 'admin_announcement',
    title: string,
    body: string,
    data?: Record<string, any>
  ): Promise<void> {
    try {
      // Get all active club members
      const { data: members } = await this.supabase
        .from('user_club_memberships')
        .select('user_id')
        .eq('club_id', clubId)
        .eq('status', 'active');

      if (!members || members.length === 0) return;

      // Check notification preferences for each member
      const memberIds = members.map(m => m.user_id);
      const { data: preferences } = await this.supabase
        .from('user_notification_preferences')
        .select('user_id, channel_notifications')
        .in('user_id', memberIds)
        .eq('club_id', clubId);

      // Filter members who want channel notifications
      const notifyMembers = members.filter(member => {
        const userPrefs = preferences?.find(p => p.user_id === member.user_id);
        // Default to true if no preferences set
        return userPrefs?.channel_notifications !== false;
      });

      if (notifyMembers.length === 0) return;

      // Send notifications using the notification service
      const { NotificationsService } = await import('./notifications.service');
      const notificationService = new NotificationsService();

      for (const member of notifyMembers) {
        await notificationService.sendNotification({
          user_id: member.user_id,
          title,
          body,
          notification_type: notificationType,
          data
        });
      }
    } catch (error) {
      console.error('Failed to send channel notifications:', error);
      // Don't throw - notification failures shouldn't break channel operations
    }
  }

  // =====================
  // CHANNEL SUBSCRIPTIONS (For Admin Interface)
  // =====================

  /**
   * Subscribe to channel list changes for a club
   * This is used by the admin interface to get real-time updates
   */
  subscribeToClubChannels(clubId: string, callback: (channels: ClubChannel[]) => void) {
    const channelName = `club-channels-${clubId}`;
    
    const channel = this.supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'club_channels',
          filter: `club_id=eq.${clubId}`
        },
        async () => {
          // Reload all channels when any change occurs
          try {
            const response = await this.getClubChannels({ club_id: clubId });
            if (response.success) {
              callback(response.data || []);
            }
          } catch (error) {
            console.error('Failed to reload channels in subscription:', error);
          }
        }
      )
      .subscribe();

    this.subscriptions.set(channelName, channel);
    return channel;
  }

  /**
   * Unsubscribe from club channel changes
   */
  unsubscribeFromClubChannels(clubId: string) {
    const channelName = `club-channels-${clubId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      channel.unsubscribe();
      this.subscriptions.delete(channelName);
    }
  }

  async updateLastRead(channelId: string, userId: string): Promise<void> {
    await this.supabase
      .from('club_channel_members')
      .upsert({
        channel_id: channelId,
        user_id: userId,
        last_read_at: new Date().toISOString()
      }, {
        onConflict: 'channel_id,user_id'
      });
  }

  private async getCurrentUserId(): Promise<string> {
    const { data: { user } } = await this.supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    return user.id;
  }

  private async getChannelName(channelId: string): Promise<string> {
    const { data: channel } = await this.supabase
      .from('club_channels')
      .select('name')
      .eq('id', channelId)
      .single();
    
    return channel?.name || 'Unknown Channel';
  }
}

export const clubCommunityService = ClubCommunityService.getInstance();
export default clubCommunityService;